import React, { useMemo } from 'react';
import { TransitionGroup } from 'react-transition-group';
import clsx from 'clsx';
import styles from './styles/snackbar.module.css';
import { SnackbarProvider, useSnackbar } from './snackbar-provider';
import { useFocusOnIgnore } from '../_internal/hooks';
/**
 * Snackbar представляет собой набор компонентов для организации всплывающих уведомлений.
 *
 * @example ``` tsx
 const { state, addSnack, removeSnack } = useSnackbar();
 <Snackbar>
 {state.map(({ id, title, content, icon, type, button, time, wide }) => (
          <Snack
            key={id}
            id={id}
            header={title}
            paragraph={content}
            icon={icon}
            variant={type}
            wideButton={wide}
            autoHideTimer={time ? 3000 : undefined}>
            <SnackButton onClick={() => removeSnack(id)}>Close</SnackButton>
          </Snack>
        ))}
 </Snackbar>
 * ```
 */

export const Snackbar = ({
  className,
  verticalPosition = 'bottom',
  horizontalPosition = 'center',
  children,
  ...rest
}) => {
  const {
    removeSnack
  } = useSnackbar();
  const {
    focusOnIgnoreProps
  } = useFocusOnIgnore();
  const SnackBarClassName = useMemo(() => clsx(styles.snackbar, styles[verticalPosition], styles[horizontalPosition], className), [className, verticalPosition, horizontalPosition]);
  return /*#__PURE__*/React.createElement(TransitionGroup, {
    className: SnackBarClassName,
    childFactory: child => {
      const {
        key,
        props,
        type
      } = child; // eslint-disable-next-line @typescript-eslint/naming-convention

      const {
        in: _in,
        enter,
        exit,
        onExited,
        children: nestedChildren,
        ...actualProps
      } = props;
      const transitionProps = {
        in: _in,
        enter,
        exit,
        onExited,
        removeSnack
      };
      const nestedElement = /*#__PURE__*/React.createElement(type, { ...actualProps
      }, nestedChildren);
      return /*#__PURE__*/React.createElement(SnackbarProvider, {
        key: key,
        ...transitionProps
      }, nestedElement);
    },
    "data-testid": "snackbar",
    ...focusOnIgnoreProps,
    ...rest
  }, children);
};